// Staff Availability. Day schedule: every stylist's day on one board, with walk-ins, any-stylist
// places and bookings waiting for a stylist. Stylists: the staff list as in the reference salon
// dashboard (Add/Edit staff, or the calendar button → that stylist's weekly slots).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { formatDate, formatPhone } from '../services/api';
import { nyafazEngagementPath } from '../nyafazEngagementPaths';
import {
  BackIcon,
  CalendarIcon,
  Confirm,
  DeleteButton,
  EditButton,
  Loader,
  Modal,
  PageHead,
  Pagination,
  PlusIcon,
  SearchBox,
  Spinner,
  usePagination,
} from '../components/ui';
import SlotCalendar from './Slots';
import ScheduleBoard from './ScheduleBoard';

const TABS = [
  { id: 'schedule', label: '📅 Day schedule' },
  { id: 'stylists', label: '💇 Stylists & weekly slots' },
];

function StaffForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({ name: initial?.name || '', phone: initial?.phone || '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await onSave({ name: form.name.trim(), phone: form.phone.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nye-panel nye-fade" style={{ minHeight: 420 }}>
      <div className="nye-view-head" style={{ marginBottom: 30 }}>
        <button type="button" className="nye-back-btn" onClick={onCancel} aria-label="Back">
          <BackIcon />
        </button>
        <div className="nye-view-title">{initial ? 'Edit Staff Member' : 'Add Staff Member'}</div>
      </div>

      <div className="nye-form-grid">
        <label className="nye-field">
          <span className="nye-label">
            Full Name <span className="nye-req">*</span>
          </span>
          <input className="nye-input" value={form.name} onChange={set('name')} placeholder="Enter Name" autoFocus />
        </label>
        <label className="nye-field">
          <span className="nye-label">Phone Number</span>
          <input className="nye-input" value={form.phone} onChange={set('phone')} placeholder="Enter Number" />
        </label>
      </div>
      <div className="nye-hint nye-mt-16">
        Customers can ask for this stylist by name on WhatsApp or on a voice call. After saving, use the calendar button to generate their
        slots.
      </div>

      <div className="nye-form-actions" style={{ marginTop: 48 }}>
        <button type="button" className="nye-btn nye-btn--muted" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="nye-btn nye-btn--primary" onClick={save} disabled={saving}>
          {saving ? <Spinner light /> : null}
          {initial ? 'Update Staff' : 'Add Staff'}
        </button>
      </div>
    </div>
  );
}

export default function Staff() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('list'); // list | add | edit | slots
  const [active, setActive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState('schedule');

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [rows, sum] = await Promise.all([api.listStaff(), api.slotsSummary()]);
      setStaff(rows);
      setSummary(sum);
    } catch (e) {
      toast.error(e.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
  }, []);

  const staffRows = Array.isArray(staff) ? staff : [];
  const activeStaff = staffRows.filter((s) => s.active);
  const inactiveStaff = staffRows.filter((s) => !s.active);
  const filtered = activeStaff.filter((s) => {
    const q = search.trim().toLowerCase();
    return !q || s.name.toLowerCase().includes(q) || (s.phone || '').includes(q);
  });
  const { page, setPage, pageSize, setPageSize, paginated, totalPages } = usePagination(filtered);
  const until = summary?.generated_until || {};

  const saveStaff = async (form) => {
    try {
      if (view === 'edit' && active) {
        await api.updateStaff(active.id, form);
        toast.success(`${form.name} updated successfully`);
      } else {
        await api.addStaff(form);
        toast.success(`${form.name} added successfully`);
      }
      setView('list');
      setActive(null);
      load(true);
    } catch (e) {
      toast.error(e.message || 'Failed to save staff');
    }
  };

  const deactivate = async () => {
    const target = confirmDelete;
    setBusyId(target.id);
    try {
      await api.updateStaff(target.id, { active: false });
      toast.success(`${target.name} removed. Booking history is kept.`);
      setConfirmDelete(null);
      load(true);
    } catch (e) {
      setConfirmDelete(null);
      if (e.status === 409) setBlocked({ name: target.name, message: e.message });
      else toast.error(e.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (member) => {
    setBusyId(member.id);
    try {
      await api.updateStaff(member.id, { active: true });
      toast.success(`${member.name} is active again`);
      load(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Loader />;

  if (view === 'add') return <StaffForm onSave={saveStaff} onCancel={() => setView('list')} />;
  if (view === 'edit' && active) return <StaffForm initial={active} onSave={saveStaff} onCancel={() => setView('list')} />;
  if (view === 'slots' && active) {
    return (
      <SlotCalendar
        owner={active}
        onBack={() => {
          setView('list');
          setActive(null);
          load(true);
        }}
      />
    );
  }

  const openSlots = (owner) => {
    setActive(owner);
    setView('slots');
  };

  const tabBar = (
    <div className="nye-seg nye-seg--page" role="tablist" aria-label="Staff Availability views">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          className={tab === t.id ? 'active' : ''}
          onClick={() => {
            setTab(t.id);
            if (t.id === 'stylists') load(true);
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  if (tab === 'schedule') {
    return (
      <div className="nye-fade">
        <PageHead title="Staff Availability" action={tabBar} />
        <ScheduleBoard />
      </div>
    );
  }

  return (
    <div className="nye-fade">
      <PageHead title="Staff Availability" action={tabBar} />
      <div>
        <div className="nye-row nye-row--between nye-row--wrap" style={{ marginBottom: 20, gap: 12 }}>
          <SearchBox value={search} onChange={setSearch} placeholder="Search staff name..." filled />
          <button type="button" className="nye-btn nye-btn--primary" onClick={() => setView('add')}>
            <PlusIcon /> Add Staff
          </button>
        </div>

        <div className="nye-table-shell">
          <div className="nye-table-wrap">
            <table className="nye-table">
              <thead>
                <tr>
                  {['Staff Profile', 'Name', 'Role', 'Phone', 'Open Slots', 'Generate Slot', 'Status', 'Edit', 'Delete'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="nye-table-empty">
                      {activeStaff.length ? 'No staff match your search.' : 'No staff yet. Click Add Staff to add your first stylist.'}
                    </td>
                  </tr>
                ) : (
                  paginated.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="nye-profile-dot">{(s.name || '?')[0].toUpperCase()}</div>
                      </td>
                      <td>
                        <div className="nye-td-strong">{s.name}</div>
                        {s.upcoming_appointments ? (
                          <div className="nye-td-muted">
                            {s.upcoming_appointments} upcoming appointment{s.upcoming_appointments > 1 ? 's' : ''}
                          </div>
                        ) : null}
                      </td>
                      <td>Stylist</td>
                      <td>{s.phone ? formatPhone(s.phone) : <span className="nye-td-muted">—</span>}</td>
                      <td>
                        <span className="nye-strong">{s.free_slots}</span>
                        {until[String(s.id)] ? <div className="nye-td-muted">until {formatDate(until[String(s.id)])}</div> : null}
                      </td>
                      <td>
                        <button type="button" className="nye-icon-action nye-icon-action--solid" title="Generate slots" onClick={() => openSlots(s)}>
                          <CalendarIcon />
                        </button>
                      </td>
                      <td>
                        <span className="nye-pill nye-pill--ok nye-pill--square">Active</span>
                      </td>
                      <td>
                        <EditButton
                          onClick={() => {
                            setActive(s);
                            setView('edit');
                          }}
                        />
                      </td>
                      <td>
                        <DeleteButton onClick={() => setConfirmDelete(s)} disabled={busyId === s.id} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination attached page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} totalPages={totalPages} />
        </div>
      </div>

      {inactiveStaff.length > 0 ? (
        <div style={{ marginTop: 24 }}>
          <div className="nye-section-label" style={{ color: 'var(--nye-danger)' }}>
            🗑 Removed staff ({inactiveStaff.length}) — not offered to customers
          </div>
          <div className="nye-table-shell" style={{ opacity: 0.9 }}>
            <table className="nye-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inactiveStaff.map((s) => (
                  <tr key={s.id}>
                    <td className="nye-td-strong">{s.name}</td>
                    <td>{s.phone ? formatPhone(s.phone) : '—'}</td>
                    <td>
                      <span className="nye-pill nye-pill--danger nye-pill--square">Inactive</span>
                    </td>
                    <td>
                      <button type="button" className="nye-btn nye-btn--outline nye-btn--xs" onClick={() => reactivate(s)} disabled={busyId === s.id}>
                        Reactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <Confirm
          title="Delete staff?"
          message={`Delete "${confirmDelete.name}"? Their free slots stop being offered. Past bookings keep the stylist name.`}
          confirmLabel="Delete"
          busy={busyId === confirmDelete.id}
          onConfirm={deactivate}
          onCancel={() => setConfirmDelete(null)}
        />
      ) : null}

      {blocked ? (
        <Modal title="Cannot Delete Staff" onClose={() => setBlocked(null)} size="sm" top>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
          <div className="nye-muted" style={{ marginBottom: 14, lineHeight: 1.6 }}>
            {blocked.message}
          </div>
          <div className="nye-callout" style={{ marginBottom: 18 }}>
            💡 Go to <strong>Appointments</strong>, find {blocked.name}'s upcoming bookings, mark them service taken or no-show, then come back to
            delete.
          </div>
          <div className="nye-row" style={{ gap: 10 }}>
            <button type="button" className="nye-btn nye-btn--outline" style={{ flex: 1 }} onClick={() => setBlocked(null)}>
              Close
            </button>
            <button type="button" className="nye-btn nye-btn--primary" style={{ flex: 1 }} onClick={() => navigate(nyafazEngagementPath('appointments'))}>
              Go to Appointments
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
